import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { ReviewFlag, Station } from '../api/types'
import { AlarmsPanel } from '../components/Alarms'
import { OverviewCustomizer, useOverviewLayout } from '../components/OverviewCustomizer'
import { AttentionQueue } from '../components/overview/AttentionQueue'
import { RefreshIcon } from '../components/overview/icons'
import { OverviewToolbar } from '../components/overview/OverviewToolbar'
import { StationList } from '../components/overview/StationList'
import { StatusCards } from '../components/overview/StatusCards'
import { type AlarmFilter, alarmPill, alarmsByStation, alarmsChecked } from '../lib/alerts'
import { useAuth } from '../lib/auth'
import { systemStatus } from '../lib/freshness'
import { checksStale } from '../lib/health'
import {
  activeIssueFlags, issueOccurrence, knownCounts, alarmsInScope, alarmSummary, categoryCounts, healthChecksText, hiddenAlarmsText, hiddenNoDataText, issuesInScope,
  networkStatus, newestCheck, type OverviewFilters, overviewChecksStale, overviewFilterParams, overviewIssues,
  readOverviewFilters, refreshNotice, searchIssues, stationsNeedingAttention, stationView,
} from '../lib/overviewStatus'
import { canEdit, hiddenNoData, projectFilter, projectFilterNotice } from '../lib/projects'
import { formatDateTime } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import { usePolled } from '../lib/usePolled'
import './overview.css'
import './overview-operations.css'
import './overview-stations.css'

const REFRESH_MS = 30_000

/** Home: the network at a glance, what needs attention, open alarms and
 *  every station, on one page. It absorbed the Stations page (owner,
 *  2026-10-22), whose /stations links forward here with their filters. */
export function Overview() {
  const { user } = useAuth()
  // Polled, not fetched once: a page left open must not go on saying
  // "normal" after a station goes silent or the checks stop, and must show
  // an alarm raised after it loaded.
  const stations = usePolled(() => api.stations(), [], REFRESH_MS)
  const alarms = usePolled(() => api.alerts.alarms(), [], REFRESH_MS)
  // The reader's projects (only theirs reach here): the project filter's
  // choices, and whether they may set alert rules on any station.
  const projectList = useAsync(() => api.projects.list(), [])
  const [params, setParams] = useSearchParams()
  const search = useRef<HTMLInputElement>(null)
  const { layout, save: saveLayout } = useOverviewLayout()
  const [alarmFilter, setAlarmFilter] = useState<AlarmFilter>('all')
  // Review flags are shared with everyone who sees the Overview, with who
  // flagged each (db/035; the owner's choice, 2026-10-23).
  const [flagList, setFlagList] = useState<ReviewFlag[]>([])
  const [flagError, setFlagError] = useState('')
  useEffect(() => {
    api.reviewFlags.list().then(data => setFlagList(data.flags),
      () => setFlagError('Review flags could not be loaded.'))
  }, [])

  const filters = readOverviewFilters(params)
  // The address carries every filter. Typing replaces the history entry
  // rather than adding one per keystroke; a card, a source or a project is
  // a real step and pushes one.
  const update = (patch: Partial<OverviewFilters>, replace = false) =>
    setParams(overviewFilterParams({ ...filters, ...patch }), { replace })

  const now = new Date()
  const data = stations.data ?? []
  const loading = !stations.data && stations.loading
  const failed = !stations.data && !!stations.error
  // Each station's own checks, and none at all while the list cannot
  // refresh: a failed poll cannot vouch for a station (overviewChecksStale).
  const staleOf = (station: Station) => overviewChecksStale(station, now, !stations.error)
  const statusOf = (station: Station) => systemStatus(station, staleOf(station))
  const byCode = new Map(data.map(station => [station.code, station]))

  // The project filter first: every section and count below follows it,
  // and none of a station's statuses change with it (lib/projects byProject).
  const projects = projectList.data?.projects ?? []
  const filter = projectFilter(filters.project, projectList.data?.projects, projectList.error)
  const scope = filter.name
  const filterNote = projectFilterNotice(filter)

  const allAlarms = alarms.data?.alarms
  // Two questions: did the rule monitor run (its own checked_at), and are
  // these figures this minute's (that, and the last request answered)? A
  // fetch that failed here must not be blamed on the monitor.
  const rulesChecked = alarmsChecked(alarms.data?.checked_at, now)
  const rulesCurrent = !!alarms.data && rulesChecked && !alarms.error
  // Until the first alarm request answers, the rules are unheard from, not
  // unchecked: Monitoring delayed waits rather than counting them.
  const rulesKnown = !!alarms.data || !!alarms.error
  const scopedAlarms = allAlarms && alarmsInScope(allAlarms, byCode, scope)
  const outsideAlarms = allAlarms && scopedAlarms ? allAlarms.filter(alarm => !scopedAlarms.includes(alarm)) : []

  const issues = issuesInScope(overviewIssues(data, allAlarms ?? [], now, rulesCurrent, !stations.error, rulesKnown), byCode, scope)
  // A flag holds for the incident it was made for and no longer (the
  // server's expires_at, the issue's occurrence): see activeIssueFlags.
  const flags = activeIssueFlags(issues, flagList, now)
  const details = new Map(flagList.filter(flag => flags.has(flag.issue_key)).map(flag => [flag.issue_key, flag]))
  const searched = searchIssues(issues, byCode, filters.q)
  // The station list's attention switch keeps the stations the queue lists, so
  // the page has one answer to "what needs attention": a known issue is in neither,
  // except that a station with an open alarm stays, flagged or not.
  const view = stationView(data, filters, scope, stationsNeedingAttention(issues, flags))
  // A project filter must never hide a silent station without saying so.
  const hidden = scope === null ? [] : hiddenNoData(data, view.inProject, station => statusOf(station).status === 'no_data')
  const checkedAt = newestCheck(data)
  const canManageRules = user?.role === 'admin' || projects.some(project => canEdit(project.my_role))
  const problem = refreshNotice({ error: stations.error, loaded: !!stations.data }, { error: alarms.error, loaded: !!alarms.data })
  const nothingShown = !layout.summary && !layout.attention && !layout.alarms && !layout.registry

  // Each answers whether it saved, so the form under the row closes only
  // when it did.
  const saveFlag = async (save: () => Promise<{ flags: ReviewFlag[] }>): Promise<boolean> => {
    try {
      setFlagList((await save()).flags); setFlagError('')
      return true
    } catch (cause) {
      setFlagError(cause instanceof Error ? cause.message : 'The review flag could not be saved.')
      return false
    }
  }
  const flagIssue = (id: string, note: string) => {
    const issue = issues.find(item => item.id === id)
    return issue ? saveFlag(() => api.reviewFlags.flag(id, note, issueOccurrence(issue)))
      : saveFlag(() => Promise.reject(new Error('This issue has cleared. Refresh the queue.')))
  }
  const refresh = () => {
    stations.reload(); alarms.reload()
    // Only a failed list: reloading a good one would lift the project
    // filter while it loads again.
    if (projectList.error) projectList.reload()
  }

  return <section className="overview-page overview-operations" aria-labelledby="overview-title">
    <header className="overview-heading">
      <div>
        <h1 id="overview-title" className="sr-only">Overview</h1>
      </div>
      <div className="overview-heading-actions">
        <span className="ov-check-time">{checkedAt ? `Checked ${formatDateTime(checkedAt)} · UTC+4` : 'Awaiting checks'}</span>
        <OverviewCustomizer value={layout} onSave={saveLayout} />
        <button type="button" className="button" onClick={refresh} aria-label="Refresh monitoring overview"><RefreshIcon />Refresh</button>
      </div>
    </header>

    <OverviewToolbar search={search} query={filters.q} onQuery={q => update({ q }, true)} projects={projects}
      project={scope === null ? '' : filters.project ?? ''} onProject={project => update({ project })} />

    {problem && <p className="notice notice-warn" role="status">{problem}</p>}

    {/* A stopped check must never leave a station reading "normal": this
        says why the statuses below are not current. With no station there
        is no status to doubt, and the list says "No stations registered". */}
    {data.length > 0 && checksStale(checkedAt, now) && <div className="notice station-alert status-stale" role="status">
      <p className="station-alert-head">{healthChecksText(checkedAt)}</p>
      <p className="muted">The statuses below are not current. The checks run in ingest-slow.</p>
    </div>}

    {filter.kind === 'pending' && <p className="muted ov-filter-pending" role="status">{filterNote}</p>}
    {filterNote && filter.kind !== 'pending' && <div className="notice station-alert status-stale" role="status">
      <p className="station-alert-head">{filterNote}</p>
      <button type="button" className="button" onClick={() => update({ project: null })}>Show all projects</button>
    </div>}
    {/* What the project filter hides that must stay in sight: its No data
        stations by name (the Stations board's guard) and its open alarms,
        since the cards, the queue and the Alarms panel all follow it. */}
    {scope !== null && (hidden.length > 0 || outsideAlarms.length > 0) && <div role="status"
      className={`notice station-alert ${hidden.length || outsideAlarms.some(alarm => alarm.level === 'alarm') ? 'status-alarm' : 'status-warning'}`}>
      {hidden.length > 0 && <p className="station-alert-head">{hiddenNoDataText(hidden, scope)}</p>}
      {outsideAlarms.length > 0 && <p className="station-alert-head">{hiddenAlarmsText(outsideAlarms, scope)}</p>}
      <button type="button" className="button" onClick={() => update({ project: null })}>Show all projects</button>
    </div>}

    {layout.summary && <StatusCards
      network={stations.data ? networkStatus(view.inProject, statusOf) : null}
      alarm={alarmSummary(scopedAlarms, { current: rulesCurrent, checked: rulesChecked, loading: alarms.loading, error: alarms.error, outside: outsideAlarms.length })}
      alarmTone={rulesCurrent && scopedAlarms ? alarmPill(scopedAlarms).tone : 'none'}
      counts={categoryCounts(issues, flags)} known={knownCounts(issues, flags)} stationsFailed={!!stations.error} loading={loading}
      filter={filters.issue} onFilter={issue => update({ issue })} />}

    {layout.attention && <AttentionQueue issues={searched} filter={filters.issue} onFilter={issue => update({ issue })}
      flags={flags} details={details} onFlag={flagIssue} flagError={flagError}
      onEditNote={(id, note) => saveFlag(() => api.reviewFlags.editNote(id, note))}
      onCheck={(id, note) => saveFlag(() => api.reviewFlags.check(id, note))}
      onUnflag={id => void saveFlag(() => api.reviewFlags.unflag(id))} loading={loading || (!alarms.data && alarms.loading)}
      unavailable={!!stations.error} narrowed={!!filters.q.trim() || scope !== null} canManageRules={canManageRules} />}

    {/* The panel follows the project filter but not the search: its words
        ("No alarm is active") speak for the whole scope, so a word typed to
        find a station must not empty it. */}
    {layout.alarms && scopedAlarms && <AlarmsPanel alarms={scopedAlarms} filter={alarmFilter} onFilter={setAlarmFilter}
      checked={rulesChecked} checkedAt={alarms.data?.checked_at ?? null} />}

    {layout.registry && <StationList view={view} total={data.length}
      filtered={!!filters.q || filters.attention || !!filters.source || scope !== null}
      query={filters.q} attention={filters.attention} source={filters.source}
      onSource={source => update({ source })} onAttention={attention => update({ attention })}
      onClearSearch={() => { update({ q: '' }); search.current?.focus() }}
      statusOf={statusOf} staleOf={staleOf} alarms={alarmsByStation(scopedAlarms ?? [])} now={now}
      loading={loading} failed={failed} />}

    {nothingShown && <p className="ov-all-hidden" role="status">Every section is hidden in your layout. Use Customize to show them again.</p>}
  </section>
}
